/**
 * Workflow Persistence Service
 * Enhanced auto-save and session management for workflows
 */

import { debounce } from 'lodash';
import { WorkflowGraph } from '../types/node-definitions';
import { canvasStorageService } from './CanvasStorageService';
import { canvasSerializationService } from './CanvasSerializationService';

export interface WorkflowSession {
  id: string;
  name: string;
  lastSaved: string;
  autoSaveEnabled: boolean;
  isDirty: boolean;
  workflow: WorkflowGraph;
  metadata: {
    nodeCount: number;
    connectionCount: number;
    lastActivity: string;
    version: string;
  };
}

export interface PersistenceSettings {
  autoSaveInterval: number; // in seconds
  maxAutoSaves: number;
  enableSessionRestore: boolean;
  showUnsavedWarning: boolean;
  compressionEnabled: boolean;
}

class WorkflowPersistenceService {
  private static readonly CURRENT_SESSION_KEY = 'sengilabs-current-session';
  private static readonly RECENT_WORKFLOWS_KEY = 'sengilabs-recent-workflows';
  private static readonly SETTINGS_KEY = 'sengilabs-persistence-settings';
  private static readonly AUTO_SAVE_PREFIX = 'sengilabs-autosave-';

  private currentSession: WorkflowSession | null = null;
  private autoSaveTimer: NodeJS.Timeout | null = null;
  private isDirty = false;
  private settings: PersistenceSettings = {
    autoSaveInterval: 30, // 30 seconds
    maxAutoSaves: 5,
    enableSessionRestore: true,
    showUnsavedWarning: true,
    compressionEnabled: true
  };

  // Debounced auto-save function
  private debouncedAutoSave = debounce(this.performAutoSave.bind(this), 2000);

  constructor() {
    this.loadSettings();
    this.setupEventListeners();
  }

  /**
   * Initialize persistence service
   */
  async initialize(): Promise<void> {
    if (this.settings.enableSessionRestore) {
      await this.restoreSession();
    }
    this.startAutoSave();
  }

  /**
   * Start new workflow session
   */
  async startSession(workflow: WorkflowGraph, name?: string): Promise<string> {
    const sessionId = this.generateSessionId();
    
    this.currentSession = {
      id: sessionId,
      name: name || `Untitled Workflow ${new Date().toLocaleDateString()}`,
      lastSaved: new Date().toISOString(),
      autoSaveEnabled: true,
      isDirty: false,
      workflow: { ...workflow },
      metadata: {
        nodeCount: Object.keys(workflow.nodes).length,
        connectionCount: workflow.connections.length,
        lastActivity: new Date().toISOString(),
        version: '1.0.0'
      }
    };

    await this.saveCurrentSession();
    return sessionId;
  }

  /**
   * Update current workflow (triggers auto-save)
   */
  async updateWorkflow(workflow: WorkflowGraph): Promise<void> {
    if (!this.currentSession) {
      // Auto-start session if none exists
      await this.startSession(workflow);
      return;
    }

    this.currentSession.workflow = { ...workflow };
    this.currentSession.metadata = {
      ...this.currentSession.metadata,
      nodeCount: Object.keys(workflow.nodes).length,
      connectionCount: workflow.connections.length,
      lastActivity: new Date().toISOString()
    };
    
    this.markDirty();
    this.debouncedAutoSave();
  }

  /**
   * Save workflow manually
   */
  async saveWorkflow(name?: string): Promise<{ success: boolean; id?: string; error?: string }> {
    if (!this.currentSession) {
      return { success: false, error: 'No active session' };
    }

    try {
      // Update session name if provided
      if (name && name !== this.currentSession.name) {
        this.currentSession.name = name;
      }

      // Serialize workflow
      const serializedCanvas = canvasSerializationService.serializeCanvas(
        this.currentSession.workflow,
        {
          includeMetadata: true,
          validateConnections: true,
          optimizeSize: this.settings.compressionEnabled
        }
      );

      if (!serializedCanvas.success || !serializedCanvas.canvas) {
        return { success: false, error: 'Failed to serialize workflow' };
      }

      // Save to permanent storage
      const result = await canvasStorageService.saveCanvas(serializedCanvas.canvas, {
        backend: 'localStorage',
        compression: this.settings.compressionEnabled,
        versioning: true
      });

      if (result.success) {
        this.currentSession.lastSaved = new Date().toISOString();
        this.currentSession.isDirty = false;
        this.isDirty = false;
        
        // Update recent workflows
        await this.updateRecentWorkflows(this.currentSession);
        await this.saveCurrentSession();

        return { success: true, id: result.id };
      }

      return { success: false, error: result.error };
    } catch (error) {
      return { success: false, error: `Save failed: ${error}` };
    }
  }

  /**
   * Load workflow from storage
   */
  async loadWorkflow(workflowId: string): Promise<{ success: boolean; workflow?: WorkflowGraph; error?: string }> {
    try {
      const result = await canvasStorageService.loadCanvas(workflowId, {
        backend: 'localStorage'
      });

      if (!result.success || !result.canvas) {
        return { success: false, error: result.error };
      }

      // Deserialize canvas back to workflow
      const deserializedResult = canvasSerializationService.deserializeCanvas(result.canvas);
      
      if (!deserializedResult.success || !deserializedResult.workflow) {
        return { success: false, error: 'Failed to deserialize workflow' };
      }

      // Start new session with loaded workflow
      await this.startSession(deserializedResult.workflow, result.canvas.name);

      return { success: true, workflow: deserializedResult.workflow };
    } catch (error) {
      return { success: false, error: `Load failed: ${error}` };
    }
  }

  /**
   * Get list of recent workflows
   */
  async getRecentWorkflows(): Promise<WorkflowSession[]> {
    try {
      const recentData = localStorage.getItem(WorkflowPersistenceService.RECENT_WORKFLOWS_KEY);
      if (!recentData) return [];
      
      return JSON.parse(recentData);
    } catch (error) {
      console.error('Failed to load recent workflows:', error);
      return [];
    }
  }

  /**
   * Get current session status
   */
  getCurrentSession(): WorkflowSession | null {
    return this.currentSession;
  }

  /**
   * Check if current workflow has unsaved changes
   */
  hasUnsavedChanges(): boolean {
    return this.isDirty;
  }

  /**
   * Enable/disable auto-save
   */
  setAutoSaveEnabled(enabled: boolean): void {
    if (this.currentSession) {
      this.currentSession.autoSaveEnabled = enabled;
    }
    
    if (enabled) {
      this.startAutoSave();
    } else {
      this.stopAutoSave();
    }
  }

  /**
   * Update persistence settings
   */
  updateSettings(newSettings: Partial<PersistenceSettings>): void {
    this.settings = { ...this.settings, ...newSettings };
    this.saveSettings();
    
    // Restart auto-save if interval changed
    if (newSettings.autoSaveInterval !== undefined) {
      this.stopAutoSave();
      this.startAutoSave();
    }
  }

  /**
   * Get current settings
   */
  getSettings(): PersistenceSettings {
    return { ...this.settings };
  }

  /**
   * Clear all auto-saves and session data
   */
  async clearAllData(): Promise<void> {
    // Clear auto-saves
    const keys = Object.keys(localStorage).filter(key => 
      key.startsWith(WorkflowPersistenceService.AUTO_SAVE_PREFIX)
    );
    
    keys.forEach(key => localStorage.removeItem(key));
    
    // Clear session and recent workflows
    localStorage.removeItem(WorkflowPersistenceService.CURRENT_SESSION_KEY);
    localStorage.removeItem(WorkflowPersistenceService.RECENT_WORKFLOWS_KEY);
    
    this.currentSession = null;
    this.isDirty = false;
    this.stopAutoSave();
  }

  // Private methods

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private markDirty(): void {
    this.isDirty = true;
    if (this.currentSession) {
      this.currentSession.isDirty = true;
    }
  }

  private async performAutoSave(): Promise<void> {
    if (!this.currentSession || !this.currentSession.autoSaveEnabled || !this.isDirty) {
      return;
    }

    try {
      const autoSaveKey = `${WorkflowPersistenceService.AUTO_SAVE_PREFIX}${this.currentSession.id}`;
      const autoSaveData = {
        ...this.currentSession,
        isAutoSave: true,
        autoSaveTimestamp: new Date().toISOString()
      };

      localStorage.setItem(autoSaveKey, JSON.stringify(autoSaveData));
      
      // Clean up old auto-saves
      await this.cleanupAutoSaves();
      
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  }

  private async cleanupAutoSaves(): Promise<void> {
    const autoSaveKeys = Object.keys(localStorage)
      .filter(key => key.startsWith(WorkflowPersistenceService.AUTO_SAVE_PREFIX))
      .sort((a, b) => {
        // Sort by timestamp (newer first)
        const aData = JSON.parse(localStorage.getItem(a) || '{}');
        const bData = JSON.parse(localStorage.getItem(b) || '{}');
        return new Date(bData.autoSaveTimestamp).getTime() - new Date(aData.autoSaveTimestamp).getTime();
      });

    // Remove old auto-saves beyond max limit
    if (autoSaveKeys.length > this.settings.maxAutoSaves) {
      const keysToRemove = autoSaveKeys.slice(this.settings.maxAutoSaves);
      keysToRemove.forEach(key => localStorage.removeItem(key));
    }
  }

  private startAutoSave(): void {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
    }

    this.autoSaveTimer = setInterval(() => {
      this.debouncedAutoSave();
    }, this.settings.autoSaveInterval * 1000);
  }

  private stopAutoSave(): void {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
  }

  private async saveCurrentSession(): Promise<void> {
    if (!this.currentSession) return;

    try {
      localStorage.setItem(
        WorkflowPersistenceService.CURRENT_SESSION_KEY,
        JSON.stringify(this.currentSession)
      );
    } catch (error) {
      console.error('Failed to save current session:', error);
    }
  }

  private async restoreSession(): Promise<void> {
    try {
      const sessionData = localStorage.getItem(WorkflowPersistenceService.CURRENT_SESSION_KEY);
      if (!sessionData) return;

      const session: WorkflowSession = JSON.parse(sessionData);
      
      // Check if session is recent (within 24 hours)
      const lastActivity = new Date(session.metadata.lastActivity);
      const now = new Date();
      const hoursSinceLastActivity = (now.getTime() - lastActivity.getTime()) / (1000 * 60 * 60);
      
      if (hoursSinceLastActivity < 24) {
        this.currentSession = session;
      } else {
        // Clear old session
        localStorage.removeItem(WorkflowPersistenceService.CURRENT_SESSION_KEY);
      }
    } catch (error) {
      console.error('Failed to restore session:', error);
    }
  }

  private async updateRecentWorkflows(session: WorkflowSession): Promise<void> {
    try {
      let recentWorkflows = await this.getRecentWorkflows();
      
      // Remove existing entry if present
      recentWorkflows = recentWorkflows.filter(w => w.id !== session.id);
      
      // Add current session to beginning
      recentWorkflows.unshift({
        ...session,
        workflow: { nodes: {}, connections: [] } // Don't store full workflow in recent list
      });
      
      // Limit to 10 recent workflows
      if (recentWorkflows.length > 10) {
        recentWorkflows = recentWorkflows.slice(0, 10);
      }
      
      localStorage.setItem(
        WorkflowPersistenceService.RECENT_WORKFLOWS_KEY,
        JSON.stringify(recentWorkflows)
      );
    } catch (error) {
      console.error('Failed to update recent workflows:', error);
    }
  }

  private loadSettings(): void {
    try {
      const settingsData = localStorage.getItem(WorkflowPersistenceService.SETTINGS_KEY);
      if (settingsData) {
        this.settings = { ...this.settings, ...JSON.parse(settingsData) };
      }
    } catch (error) {
      console.error('Failed to load persistence settings:', error);
    }
  }

  private saveSettings(): void {
    try {
      localStorage.setItem(
        WorkflowPersistenceService.SETTINGS_KEY,
        JSON.stringify(this.settings)
      );
    } catch (error) {
      console.error('Failed to save persistence settings:', error);
    }
  }

  private setupEventListeners(): void {
    // Handle page unload - DISABLED: Removed annoying reload confirmation alerts
    // The auto-save functionality already preserves work, so the beforeunload warning
    // is unnecessary and creates a poor user experience
    if (typeof window !== 'undefined') {
      // Commented out the beforeunload listener to remove reload confirmation alerts
      // window.addEventListener('beforeunload', (e) => {
      //   if (this.settings.showUnsavedWarning && this.hasUnsavedChanges()) {
      //     const message = 'You have unsaved changes. Are you sure you want to leave?';
      //     e.returnValue = message;
      //     return message;
      //   }
      // });

      // Handle visibility change (browser tab switch)
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.debouncedAutoSave.flush(); // Force immediate auto-save when tab becomes hidden
        }
      });
    }
  }
}

// Singleton instance
export const workflowPersistenceService = new WorkflowPersistenceService();